import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    CreateProject,
    GetMyProjectRole,
    GetProjectDetails,
    ListProjects,
    DiscardProjectLicense,
    ListProjectDataSyncAccruedUsages,
} from "gc-web-proto/galaxycompletepb/apipb/project_api_pb";
import type { ProjectDetails } from "gc-web-proto/galaxycompletepb/apipb/domainpb/project_pb";
import { TransactionCounterInfo } from "gc-web-proto/galaxycompletepb/apipb/domainpb/license_pb";
import { LicenseVaultCounterType } from "gc-web-proto/galaxycompletepb/apipb/domainpb/enumpb/license_vault_counter_type_pb";
import { useMutationTrackAndAlertError } from "../core/data/useHooksWithErrorHandling";
import { useCurrentProject, useCurrentProjectState } from "./CurrentProjectState";
import { ProjectQueryKeys } from "../../common/QueryKeys";
import { useGrpcApiStore } from "../grpc/grpcApiStore";
import { useAppServices } from "../app/services";
import { FilterParams, PagerParams } from "gc-web-proto/galaxycompletepb/commonpb/datafilter_pb";
import { useGlobalTableSortFilterState } from "../../common/table/TableFilterState";
import { useTranslation } from "react-i18next";

export enum PROJECT_PRODUCT_TYPE {
    CMC,
    CMO,
    DATASYNC,
    DISASTER_RECOVERY,
}

export const PROJECT_FLAG_DATASYNC = "datasync";
export const PROJECT_FLAG_DISASTER_RECOVERY = "disaster-recovery";

export const useListMyProjects = (page: number, perPage: number) => {
    const apis = useGrpcApiStore();
    const sortFilterState = useGlobalTableSortFilterState().tableStateMap.get(ProjectQueryKeys.listMyProjects);
    const queryFn = async () => {
        const req = new ListProjects.Request().setIsAdmin(false).setPageParams(new PagerParams().setPerPage(perPage).setPage(page));

        if (sortFilterState.sortState) {
            req.setSortParams(sortFilterState.sortFilterConfig.sort.getSortParam(sortFilterState.sortState));
        }

        for (let filter of sortFilterState.filters || []) {
            filter.fieldConfig.addFilterToRequest(req, filter.param);
        }
        req.addFilterParams(
            new ListProjects.Request.Filter().setIsSupporting(new FilterParams.BoolFilter().setValue(false).setOp(FilterParams.BoolFilter.Op.EQUAL))
        );

        const res = await apis.projectService.listProjects(req, null);
        return res.toObject();
    };

    return useQuery({
        queryKey: [
            ProjectQueryKeys.listMyProjects,
            page,
            perPage,
            sortFilterState.sortState?.field.label,
            sortFilterState.sortState?.descending.label,
            sortFilterState.filters.map((f) => `${f.fieldConfig.label}: ${f.param.toObject().op} ${f.param.toObject().value}`),
        ],
        queryFn: queryFn,
        refetchInterval: 30 * 1000,
    });
};

export const useGetProjectShareableLicenseEnabled = () => {
    const currentProject = useCurrentProject();
    return currentProject?.getInfo().getAllowHostLicenseCapacityBorrowing();
};
export const useCreateNewProject = () => {
    const queryClient = useQueryClient();
    const apis = useGrpcApiStore();
    const { t } = useTranslation("project");

    return useMutationTrackAndAlertError(
        {
            mutationKey: ["CreateNewProject"],
            mutationFn: async (args: {
                name: string;
                description: string;
                emergencyEmail: string;
                emergencyPhone: string;
                deleteLock: boolean;
                productSelection: Array<PROJECT_PRODUCT_TYPE>;
            }) => {
                const req = new CreateProject.Request()
                    .setName(args.name)
                    .setEmergencyEmail(args.emergencyEmail)
                    .setEmergencyPhone(args.emergencyPhone)
                    .setDeleteLock(args.deleteLock)
                    .setDescription(args.description)
                    .setProductSelection(
                        new CreateProject.Request.ProductSelection()
                            .setCmo(args.productSelection.includes(PROJECT_PRODUCT_TYPE.CMO))
                            .setCmc(args.productSelection.includes(PROJECT_PRODUCT_TYPE.CMC))
                            .setUnstructuredData(args.productSelection.includes(PROJECT_PRODUCT_TYPE.DATASYNC))
                            .setDisasterRecovery(args.productSelection.includes(PROJECT_PRODUCT_TYPE.DISASTER_RECOVERY))
                    );
                return await apis.projectService.createProject(req, null);
            },
            onSuccess: async () => {
                await queryClient.refetchQueries({
                    queryKey: [ProjectQueryKeys.listMyProjects],
                });
            },
        },
        t("progress.creating")
    );
};

export const useGetProjectDetails = (projectId: string) => {
    const apis = useGrpcApiStore();
    const { projectService } = useAppServices();
    const setProjectDetails = useCurrentProjectState((s) => s.setCurrentProject);
    const req = new GetProjectDetails.Request().setProjectId(projectId);

    return useQuery({
        queryKey: [ProjectQueryKeys.getProjectDetails, projectId],
        queryFn: async () => {
            const res = await apis.projectService.getProjectDetails(req, null);
            const details = res.getProjectDetails();
            projectService.currentProject.dataLoaded = true;
            projectService.currentProject.data = details;
            setProjectDetails(details);
            return details;
        },
        refetchInterval: 30 * 1000,
    });
};

export const useGetMyProjectRole = (projectId: string) => {
    const apis = useGrpcApiStore();
    const req = new GetMyProjectRole.Request().setProjectId(projectId);
    const setMyProjectRole = useCurrentProjectState((s) => s.setMyRole);
    return useQuery({
        queryKey: [ProjectQueryKeys.getMyProjectRole],
        queryFn: async () => {
            const res = await apis.projectService.getMyProjectRole(req, null);
            const role = res.getMyRole();
            setMyProjectRole(role);
            return role;
        },
        refetchInterval: 30 * 1000,
    });
};

export const useDiscardProjectLicense = () => {
    const apis = useGrpcApiStore();
    const queryClient = useQueryClient();
    const { t } = useTranslation("project");

    return useMutationTrackAndAlertError(
        {
            mutationFn: async (params: { projectId: string; counters: TransactionCounterInfo.AsObject[] }) => {
                const req = new DiscardProjectLicense.Request();
                req.setProjectId(params.projectId);

                for (const counter of params.counters) {
                    const counterInfo = new TransactionCounterInfo();
                    counterInfo.setAmount(counter.amount);

                    const counterType = new LicenseVaultCounterType();
                    counterType.setValue(counter.counterType.value);
                    counterInfo.setCounterType(counterType);

                    req.addCounters(counterInfo);
                }

                await apis.projectService.discardProjectLicense(req, null);
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ["GetProjectLicenseVaultDetails"] });
                queryClient.invalidateQueries({ queryKey: ["ListProjectLicenseTransactions"] });
            },
        },
        t("progress.discardingLicenses")
    );
};

// Exported for ProjectProductGuard: callers that already hold a loaded ProjectDetails
// (e.g. route gates under QueryResultWrapper) must not fall back to the global
// current-project zustand store, which can lag behind the route's own query result
// during CDCPE startup / license revalidation and cause spurious "product not enabled"
// redirects. useProjectHasProductType keeps the global fallback for chrome (sidebar,
// forms) that has no loaded project in hand.
export const projectHasProductType = (project: ProjectDetails, productType: PROJECT_PRODUCT_TYPE): boolean => {
    if (!project) {
        return false;
    }

    const enabledProducts = project.getInfo().getEnabledProducts();

    if (!enabledProducts) {
        return false;
    }

    switch (productType) {
        case PROJECT_PRODUCT_TYPE.CMC:
            return enabledProducts.getCmc() || false;
        case PROJECT_PRODUCT_TYPE.CMO:
            return enabledProducts.getCmo() || false;
        case PROJECT_PRODUCT_TYPE.DATASYNC:
            return projectHasDatasyncEnabled(project) || false;
        case PROJECT_PRODUCT_TYPE.DISASTER_RECOVERY:
            return projectHasDisasterRecoveryEnabled(project) || false;
    }
};

export const useProjectHasProductType = (productType: PROJECT_PRODUCT_TYPE, project?: ProjectDetails): boolean => {
    const currentProject = useCurrentProject();
    return projectHasProductType(project || currentProject, productType);
};

const projectHasDatasyncEnabled = (project: ProjectDetails): boolean => {
    return project?.getInfo().getProjectFlagsList().includes(PROJECT_FLAG_DATASYNC) || false;
};

export const useProjectHasDatasyncEnabled = (project?: ProjectDetails) => {
    const currentProject = useCurrentProject();
    return projectHasDatasyncEnabled(project || currentProject);
};

const projectHasDisasterRecoveryEnabled = (project: ProjectDetails): boolean => {
    return project?.getInfo().getProjectFlagsList().includes(PROJECT_FLAG_DISASTER_RECOVERY) || false;
};

export const useProjectHasDisasterRecoveryEnabled = (project?: ProjectDetails) => {
    const currentProject = useCurrentProject();
    return projectHasDisasterRecoveryEnabled(project || currentProject);
};

export const useListProjectDataSyncAccruedUsages = (projectId: string, page: number, perPage: number) => {
    const apis = useGrpcApiStore();
    return useQuery({
        queryKey: [ProjectQueryKeys.listProjectDataSyncAccruedUsages, projectId, page, perPage],
        queryFn: async () => {
            const req = new ListProjectDataSyncAccruedUsages.Request()
                .setProjectId(projectId)
                .setPageParams(new PagerParams().setPerPage(perPage).setPage(page));
            const res = await apis.projectService.listProjectDataSyncAccruedUsages(req, null);
            return res.toObject();
        },
        enabled: !!projectId,
    });
};
